import { ReactNode, useCallback, useEffect, useState } from 'react'

import { Nullable } from '@types'

import SortPopover from '@modules/shortlist/components/ShortlistTable/SortPopover'
import { useSortProfileValuesMap } from '@modules/shortlist/hooks/useSortProfileValuesMap'
import { ShortlistSortProfileValue } from '@modules/shortlist/types'
import { TableRow } from '@mui/material'

import { StyledTableCell, TableCellInner } from '../../index.styles'
import { ManageTableConfigProps, ManageTableHeadProps } from '../../types'
import ManageTableProfileTitleHeadCell from './ManageTableProfileTitleHeadCell'
import { TableHeadCell } from './TableHeadCell'
import { MAP_FIELDS_TO_SORT_OPTION_VALUES, MAP_SORT_POPOVER_TITLE } from './constants'

export const ManageTableColumnHeaders = (props: ManageTableHeadProps) => {
  const { tableColumns, allProfilesSelected, onSelectAllProfiles, sort, onSortChange } = props
  const [fieldWithActiveSort, setFieldWithActiveSort] = useState<string>('profileCreatedDate')
  const [sortPopoverAnchor, setSortPopoverAnchor] = useState<Nullable<HTMLElement>>(null)
  const [fieldToSort, setFieldToSort] = useState<Nullable<string>>(null)
  const [sortProfileValues, setSortProfileValues] = useState<ShortlistSortProfileValue[]>([])

  const sortProfileValuesMap = useSortProfileValuesMap()

  const tableHeaderColumnsComponents: Record<ManageTableConfigProps['fieldName'], () => ReactNode> = {
    // eslint-disable-next-line react/no-unstable-nested-components
    profileTitle: () => (
      <ManageTableProfileTitleHeadCell
        allProfilesSelected={allProfilesSelected}
        onSelectAllProfiles={onSelectAllProfiles}
        sort={sort}
        isSortActive={!!sort && fieldWithActiveSort === 'profileTitle'}
        openSortPopover={openSortPopover}
        hasLeftOffset={false}
      />
    ),
  }

  useEffect(() => {
    if (!fieldToSort) {
      return
    }

    setSortProfileValues(MAP_FIELDS_TO_SORT_OPTION_VALUES[fieldToSort])
  }, [fieldToSort])

  const openSortPopover = useCallback(({ anchor, fieldName }: { fieldName: string; anchor: HTMLElement }) => {
    setSortPopoverAnchor(anchor)
    setFieldToSort(fieldName)
  }, [])

  const handleSortPopoverClose = useCallback(() => {
    setSortPopoverAnchor(null)
    setFieldToSort(null)
  }, [])

  const handleSortChange = useCallback(
    (newSort: ShortlistSortProfileValue) => {
      setFieldWithActiveSort(fieldToSort as string)
      onSortChange(newSort)
      handleSortPopoverClose()
      onSelectAllProfiles(false)
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [fieldToSort, handleSortPopoverClose],
  )

  return (
    <TableRow>
      {tableColumns.map((tableColumn: ManageTableConfigProps) => {
        const { columnTitle, restCellProps = {}, fieldName } = tableColumn
        const cellComponent = tableHeaderColumnsComponents[fieldName]

        return (
          <StyledTableCell
            // eslint-disable-next-line react/jsx-props-no-spreading
            {...restCellProps}
            $header
            key={`column-head-${String(fieldName)}`}
            data-testid={`head-${String(fieldName)}_testid`}
          >
            <TableCellInner>
              {cellComponent ? (
                cellComponent()
              ) : (
                <TableHeadCell
                  columnTitle={columnTitle}
                  isSortActive={!!sort && fieldWithActiveSort === fieldName}
                  sortOrder={sortProfileValuesMap[sort].order}
                  fieldName={fieldName as string}
                  openSortPopover={openSortPopover}
                />
              )}
            </TableCellInner>
          </StyledTableCell>
        )
      })}
      <SortPopover
        id="shortlist-sort-popover"
        anchor={sortPopoverAnchor}
        title={MAP_SORT_POPOVER_TITLE[fieldToSort as keyof typeof MAP_SORT_POPOVER_TITLE]}
        setAnchor={setSortPopoverAnchor}
        sortProfileValues={sortProfileValues}
        value={sort}
        onChange={handleSortChange}
        onCancel={handleSortPopoverClose}
        onConfirm={handleSortPopoverClose}
        onClose={handleSortPopoverClose}
      />
    </TableRow>
  )
}
